import React, { useState, useEffect } from 'react';
import presetApi from '@services/presetApi.jsx';
import themeLayoutMappingApi from '@services/themeLayoutMappingApi.jsx';
import '@assets/js/common/table';
import { getTimerRedirectOptions, migrateTimerRedirectValue } from '../utils/pageNavigationUtils';

const PresetLayoutSetting = ({ preset, presetPageMasters }) => {
    const VITE_ADMIN_BACKEND_API_URL = import.meta.env.VITE_ADMIN_BACKEND_API_URL;

    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingLayouts, setIsLoadingLayouts] = useState(false);

    const searchParams = new URLSearchParams(location.search);
    const presetId = searchParams.get('presetId');
    const [presetLayoutPage, setPresetLayoutPage] = useState(null);

    // 폼 데이터 상태
    const [formData, setFormData] = useState({
        enable1cutLayout: true,
        enable2cutLayout: true,
        enable3cutLayout: true,
        enable4cutLayout: true,
        timerSeconds: 10,
        timerTimeoutRedirectUrl: 'start',
        autoSelectLayoutId: null,
        isActive: true
    });

    // 타이머 관련
    const [timer, setTimer] = useState(10);
    const [timeOverPage, setTimeOverPage] = useState('start');
    const [layoutFilter, setLayoutFilter] = useState('all');
    
    // 타이머 리다이렉트 옵션
    const [timerRedirectOptions, setTimerRedirectOptions] = useState([]);

    // 테마 레이아웃 목록
    const [themeLayouts, setThemeLayouts] = useState([]);
    const [layouts, setLayouts] = useState([]);

    // 테이블 선택 관련
    const [selectAll, setSelectAll] = useState(false);

    // 모달 상태 관리
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [currentLayout, setCurrentLayout] = useState(null);

    // 페이지 데이터 로드
    useEffect(() => {
        loadLayoutSelectPageData();
    }, [presetId]);

    useEffect(() => {
        if(presetLayoutPage?.themeId
            && presetLayoutPage?.themePageMasterId)
        {
            loadThemeLayouts();
        }
    }, [presetLayoutPage]);
    
    // 타이머 리다이렉트 옵션 업데이트
    useEffect(() => {
        if (presetPageMasters && presetPageMasters.length > 0) {
            const options = getTimerRedirectOptions('layout', presetPageMasters);
            setTimerRedirectOptions(options);
        }
    }, [presetPageMasters]);

    // 레이아웃 선택 페이지 데이터 로드
    const loadLayoutSelectPageData = async () => {
        try {
            setIsLoading(true);
            const layoutPage = await presetApi.getLayoutSelectPage(presetId);
            
            setPresetLayoutPage(layoutPage);
            
            // 기존 timerTimeoutRedirectUrl 값을 새로운 형식으로 마이그레이션
            const migratedRedirectValue = migrateTimerRedirectValue(
                layoutPage.timerTimeoutRedirectUrl, 
                'layout'
            );
            
            setFormData({
                enable1cutLayout: layoutPage.enable1cutLayout ?? true,
                enable2cutLayout: layoutPage.enable2cutLayout ?? true,
                enable3cutLayout: layoutPage.enable3cutLayout ?? true,
                enable4cutLayout: layoutPage.enable4cutLayout ?? true,
                timerSeconds: layoutPage.timerSeconds ?? 10,
                timerTimeoutRedirectUrl: migratedRedirectValue,
                autoSelectLayoutId: layoutPage.autoSelectLayoutId ?? null,
                isActive: layoutPage.isActive ?? true
            });
            
            setTimer(layoutPage.timerSeconds ?? 10);
            setTimeOverPage(migratedRedirectValue);
        } catch (error) {
            console.error('레이아웃 선택 페이지 데이터 로드 실패:', error);
            // 에러 시 기본값 유지
        } finally {
            setIsLoading(false);
        }
    };

    // 테마 레이아웃 목록 로드
    const loadThemeLayouts = async () => {
        try {
            setIsLoadingLayouts(true);
            const mappings = await themeLayoutMappingApi.getThemeLayoutMappings(presetLayoutPage?.themeId);

            const formattedLayouts = mappings.map((mapping, index) => ({
                id: mapping.id,
                mappingId: mapping.id,
                photoLayoutId: mapping.photoLayoutId,
                name: mapping.layoutName,
                type: mapping.layoutType,
                direction: mapping.layoutDirection,
                typeDescription: mapping.layoutTypeDescription || mapping.layoutType,
                directionDescription: mapping.layoutDirectionDescription || mapping.layoutDirection,
                selected: false,
                // 리소스 ID 저장 (이미지 URL 구성용)
                customDefaultImageResourceId: mapping.customDefaultImageResourceId,
                customHoverImageResourceId: mapping.customHoverImageResourceId,
                layoutDataResourceId: mapping.layoutDataResourceId,
                displayOrder: mapping.displayOrder,
                isActive: mapping.isActive,
                themeId: mapping.themeId
            }));

            setThemeLayouts(formattedLayouts);
            // Modified: 2025-01-27 [현재 시간 추가 필요] - 필터링 로직 추가
            // 초기 로드 시 필터 적용
            applyLayoutFilter(formattedLayouts, layoutFilter);
            
            // 레이아웃이 있고 현재 선택된 값이 없으면 첫 번째 레이아웃 자동 선택
            if (formattedLayouts.length > 0 && !formData.autoSelectLayoutId) {
                setFormData(prev => ({
                    ...prev,
                    autoSelectLayoutId: formattedLayouts[0].id
                }));
            }
        } catch (error) {
            console.error('테마 레이아웃 목록 로드 실패:', error);
            setThemeLayouts([]);
            setLayouts([]);
        } finally {
            setIsLoadingLayouts(false);
        }
    };

    // Modified: 2025-01-27 [현재 시간 추가 필요] - 레이아웃 필터링 함수 추가
    // Modified: 2025-01-27 [현재 시간 추가 필요] - 1컷, 2컷, 3컷, 4컷 필터 옵션 추가
    const applyLayoutFilter = (layoutsList, filterValue) => {
        if (filterValue === 'all') {
            setLayouts(layoutsList);
        } else if (filterValue === 'landscape' || filterValue === '가로') {
            setLayouts(layoutsList.filter(layout => 
                layout.direction === 'landscape' || 
                layout.direction === '가로' ||
                layout.directionDescription === '가로' ||
                layout.directionDescription === 'landscape'
            ));
        } else if (filterValue === 'portrait' || filterValue === '세로') {
            setLayouts(layoutsList.filter(layout => 
                layout.direction === 'portrait' || 
                layout.direction === '세로' ||
                layout.directionDescription === '세로' ||
                layout.directionDescription === 'portrait'
            ));
        } else if (filterValue === '1cut' || filterValue === '1컷') {
            setLayouts(layoutsList.filter(layout => 
                layout.type === '1cut' || 
                layout.type === '1컷' ||
                layout.typeDescription === '1cut' ||
                layout.typeDescription === '1컷' ||
                (layout.typeDescription && layout.typeDescription.includes('1컷')) ||
                (layout.type && layout.type.includes('1'))
            ));
        } else if (filterValue === '2cut' || filterValue === '2컷') {
            setLayouts(layoutsList.filter(layout => 
                layout.type === '2cut' || 
                layout.type === '2컷' ||
                layout.typeDescription === '2cut' ||
                layout.typeDescription === '2컷' ||
                (layout.typeDescription && layout.typeDescription.includes('2컷')) ||
                (layout.type && layout.type.includes('2'))
            ));
        } else if (filterValue === '3cut' || filterValue === '3컷') {
            setLayouts(layoutsList.filter(layout => 
                layout.type === '3cut' || 
                layout.type === '3컷' ||
                layout.typeDescription === '3cut' ||
                layout.typeDescription === '3컷' ||
                (layout.typeDescription && layout.typeDescription.includes('3컷')) ||
                (layout.type && layout.type.includes('3'))
            ));
        } else if (filterValue === '4cut' || filterValue === '4컷') {
            setLayouts(layoutsList.filter(layout => 
                layout.type === '4cut' || 
                layout.type === '4컷' ||
                layout.typeDescription === '4cut' ||
                layout.typeDescription === '4컷' ||
                (layout.typeDescription && layout.typeDescription.includes('4컷')) ||
                (layout.type && layout.type.includes('4'))
            ));
        } else {
            setLayouts(layoutsList);
        }
    };

    // Modified: 2025-01-27 [현재 시간 추가 필요] - 필터 변경 시 필터링 적용
    useEffect(() => {
        if (themeLayouts.length > 0) {
            applyLayoutFilter(themeLayouts, layoutFilter);
        }
    }, [layoutFilter, themeLayouts]);

    // 레이아웃 타입 토글 핸들러
    const handleLayoutTypeToggle = (type) => {
        const newFormData = {
            ...formData,
            [type]: !formData[type]
        };
        setFormData(newFormData);
    };

    // 타이머 조절 핸들러
    const handleTimerChange = (action) => {
        let newTimer;
        if (action === 'increase') {
            newTimer = Math.min(timer + 1, 60);
        } else {
            newTimer = Math.max(timer - 1, 1);
        }
        
        setTimer(newTimer);
        setFormData({
            ...formData,
            timerSeconds: newTimer
        });
    };

    // 타임 오버 페이지 변경 핸들러
    const handleTimeOverPageChange = (e) => {
        const newPage = e.target.value;
        setTimeOverPage(newPage);
        setFormData({
            ...formData,
            timerTimeoutRedirectUrl: newPage
        });
    };

    // 자동 선택 레이아웃 변경 핸들러
    const handleAutoSelectLayoutChange = (e) => {
        const layoutId = e.target.value ? parseInt(e.target.value) : null;
        // 값이 비어있고 레이아웃 목록이 있으면 첫 번째 레이아웃 선택
        const finalLayoutId = layoutId || (themeLayouts.length > 0 ? themeLayouts[0].id : null);
        setFormData({
            ...formData,
            autoSelectLayoutId: finalLayoutId
        });
    };

    // 체크박스 선택 핸들러
    const handleCheckboxChange = (id) => {
        setLayouts(prevLayouts =>
            prevLayouts.map(layout =>
                layout.id === id ? { ...layout, selected: !layout.selected } : layout
            )
        );
    };

    // 전체 선택 핸들러
    const handleSelectAll = () => {
        setSelectAll(!selectAll);
        setLayouts(prevLayouts =>
            prevLayouts.map(layout => ({ ...layout, selected: !selectAll }))
        );
    };

    // 상세정보 아이콘 클릭 핸들러
    const handleInfoClick = (layout) => {
        setCurrentLayout(layout);
        setShowDetailModal(true);
    };

    // 저장 이벤트 리스너
    useEffect(() => {
        const eventName = `savePagePreset_layout`;

        const saveHandler = async (event) => {
            if (!preset?.id) {
                alert("프리셋 정보가 없습니다.");
                return;
            }

            try {
                setIsLoading(true);

                // 저장 전 자동 선택 레이아웃 검증
                let autoSelectLayoutId = formData.autoSelectLayoutId;
                if (!autoSelectLayoutId && themeLayouts.length > 0) {
                    autoSelectLayoutId = themeLayouts[0].id;
                }

                const dataToSave = {
                    presetId: event.detail.presetId,
                    enable1cutLayout: formData.enable1cutLayout,
                    enable2cutLayout: formData.enable2cutLayout,
                    enable3cutLayout: formData.enable3cutLayout,
                    enable4cutLayout: formData.enable4cutLayout,
                    timerSeconds: formData.timerSeconds,
                    timerTimeoutRedirectUrl: formData.timerTimeoutRedirectUrl,
                    autoSelectLayoutId: autoSelectLayoutId,
                    isActive: formData.isActive
                };
                
                const response = await presetApi.saveLayoutSelectPage(event.detail.presetId, dataToSave);
                console.log("레이아웃 선택 페이지 저장 성공:", response);

                // 저장 후 최신 데이터로 화면 갱신
                if (response) {
                    setFormData({
                        enable1cutLayout: response.enable1cutLayout ?? true,
                        enable2cutLayout: response.enable2cutLayout ?? true,
                        enable3cutLayout: response.enable3cutLayout ?? true,
                        enable4cutLayout: response.enable4cutLayout ?? true,
                        timerSeconds: response.timerSeconds ?? 10,
                        timerTimeoutRedirectUrl: response.timerTimeoutRedirectUrl ?? 'start',
                        autoSelectLayoutId: response.autoSelectLayoutId ?? null,
                        isActive: response.isActive ?? true
                    });
                    setPresetLayoutPage(response);
                }

            } catch (error) {
                console.error("레이아웃 선택 페이지 저장 실패:", error);
                alert("레이아웃 선택 페이지 저장 중 오류가 발생했습니다.");
            } finally {
                setIsLoading(false);
            }
        };

        document.addEventListener(eventName, saveHandler);

        return () => {
            document.removeEventListener(eventName, saveHandler);
        };
    }, [preset?.id, formData]);

    // 테이블 초기화
    useEffect(() => {
        if (window.tableManager) {
            window.tableManager.initTable({
                tableSelector: '.common-table',
                filterSelector: '#layout-filter',
                columnWidthOptions: {
                    equalWidth: false,
                    firstColumnWidth: 5,
                    lastColumnWidth: 5,
                    fixedColumns: {
                        1: 10,  // NO 열
                        2: 20,  // 레이아웃명 열
                        3: 15,  // 타입 열
                        4: 15,  // 방향 열
                        5: 10   // 상세정보 열
                    }
                }
            });
        }
    }, [layouts]);

    if (isLoading) {
        return <div className="preset-settings">로딩 중...</div>;
    }

    return (
        <div className="preset-settings">
            {/* 타이머 설정 섹션 */}
            <div className="settings-box">
                <h4 className="settings-title">타이머</h4>
                <div className="settings-row">
                    <div className="settings-label">타이머 시간</div>
                    <div className="timer-control-container">
                        <button 
                            className="timer-btn" 
                            onClick={() => handleTimerChange('decrease')}
                        >
                            <i className="ri-arrow-down-s-fill"></i>
                        </button>
                        <input 
                            type="text" 
                            className="timer-input" 
                            value={timer}
                            readOnly
                        />
                        <button 
                            className="timer-btn" 
                            onClick={() => handleTimerChange('increase')}
                        >
                            <i className="ri-arrow-up-s-fill"></i>
                        </button>
                        <span className="timer-unit">초</span>
                    </div>
                </div>
                <div className="settings-row">
                    <div className="settings-label">타임 오버 이동 페이지</div>
                    <div className="page-select-container">
                        <select 
                            className="page-select"
                            value={timeOverPage}
                            onChange={handleTimeOverPageChange}
                        >
                            {timerRedirectOptions.map(option => (
                                <option 
                                    key={option.value} 
                                    value={option.value}
                                    disabled={!option.available}
                                >
                                    {option.label}
                                </option>
                            ))}
                        </select>
                        <i className="ri-arrow-down-s-line select-arrow"></i>
                    </div>
                </div>
            </div>

            {/* 사용 레이아웃 설정 섹션 */}
            <div className="settings-box">
                <h4 className="settings-title">사용 레이아웃 설정</h4>
                <div className="settings-row">
                    <div className="settings-label">1컷</div>
                    <div className="toggle-switch-container">
                        <label className="toggle-switch">
                            <input 
                                type="checkbox" 
                                checked={formData.enable1cutLayout}
                                onChange={() => handleLayoutTypeToggle('enable1cutLayout')}
                            />
                            <span className="toggle-slider"></span>
                        </label>
                    </div>
                </div>
                <div className="settings-row">
                    <div className="settings-label">2컷</div>
                    <div className="toggle-switch-container">
                        <label className="toggle-switch">
                            <input 
                                type="checkbox" 
                                checked={formData.enable2cutLayout}
                                onChange={() => handleLayoutTypeToggle('enable2cutLayout')}
                            />
                            <span className="toggle-slider"></span>
                        </label>
                    </div>
                </div>
                <div className="settings-row">
                    <div className="settings-label">3컷</div>
                    <div className="toggle-switch-container">
                        <label className="toggle-switch">
                            <input 
                                type="checkbox" 
                                checked={formData.enable3cutLayout}
                                onChange={() => handleLayoutTypeToggle('enable3cutLayout')}
                            />
                            <span className="toggle-slider"></span>
                        </label>
                    </div>
                </div>
                <div className="settings-row">
                    <div className="settings-label">4컷</div>
                    <div className="toggle-switch-container">
                        <label className="toggle-switch">
                            <input 
                                type="checkbox" 
                                checked={formData.enable4cutLayout}
                                onChange={() => handleLayoutTypeToggle('enable4cutLayout')}
                            />
                            <span className="toggle-slider"></span>
                        </label>
                    </div>
                </div>
            </div>

            {/* 페이지 스킵 시 레이아웃 자동 선택 섹션 */}
            <div className="settings-box">
                <h4 className="settings-title">페이지 스킵 시 레이아웃 자동 선택</h4>
                <div className="option-select-container">
                    <select 
                        className="option-select"
                        value={formData.autoSelectLayoutId || (themeLayouts.length > 0 ? themeLayouts[0].id : '')}
                        onChange={handleAutoSelectLayoutChange}
                    >
                        {themeLayouts.length > 0 ? (
                            themeLayouts.map(layout => (
                                <option key={layout.id} value={layout.id}>
                                    {layout.name} ({layout.typeDescription} {layout.directionDescription})
                                </option>
                            ))
                        ) : (
                            <option value="">없음</option>
                        )}
                    </select>
                    <i className="ri-arrow-down-s-line select-arrow"></i>
                </div>
            </div>

            {/* 레이아웃 데이터 섹션 헤더 */}
            <div className="data-section-header">
                <div className="data-section-title">
                    <h4 className="section-title">레이아웃 목록</h4>
                    {isLoadingLayouts && <span className="loading-text">(로딩 중...)</span>}
                </div>
                <div className="filter-dropdown-container">
                    <div className="filter-dropdown">
                        <select 
                            id="layout-filter"
                            value={layoutFilter}
                            onChange={(e) => setLayoutFilter(e.target.value)}
                        >
                            <option value="all">전체</option>
                            <option value="가로">가로</option>
                            <option value="세로">세로</option>
                            <option value="1컷">1컷</option>
                            <option value="2컷">2컷</option>
                            <option value="3컷">3컷</option>
                            <option value="4컷">4컷</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* 데이터 테이블 섹션 */}
            <div className="common-table-container">
                <table className="common-table">
                    <thead>
                        <tr>
                            {/*<th>*/}
                            {/*    <div className="checkbox-container">*/}
                            {/*        <input */}
                            {/*            type="checkbox" */}
                            {/*            id="select-all" */}
                            {/*            checked={selectAll}*/}
                            {/*            onChange={handleSelectAll}*/}
                            {/*        />*/}
                            {/*        <label htmlFor="select-all" className="custom-checkbox"></label>*/}
                            {/*    </div>*/}
                            {/*</th>*/}
                            <th>NO</th>
                            <th>레이아웃명</th>
                            <th>타입</th>
                            <th>방향</th>
                            <th>상세정보</th>
                        </tr>
                    </thead>
                    <tbody>
                        {layouts.length === 0 ? (
                            <tr>
                                <td colSpan="5" className="text-center">
                                    {isLoadingLayouts ? '레이아웃 목록을 로딩 중입니다...' : '등록된 레이아웃이 없습니다.'}
                                </td>
                            </tr>
                        ) : (
                            layouts.map((layout, index) => (
                                <tr key={layout.id}>
                                    {/*<td>*/}
                                    {/*    <div className="checkbox-container">*/}
                                    {/*        <input */}
                                    {/*            type="checkbox" */}
                                    {/*            id={`layout-${layout.id}`}*/}
                                    {/*            checked={layout.selected}*/}
                                    {/*            onChange={() => handleCheckboxChange(layout.id)}*/}
                                    {/*        />*/}
                                    {/*        <label htmlFor={`layout-${layout.id}`} className="custom-checkbox"></label>*/}
                                    {/*    </div>*/}
                                    {/*</td>*/}
                                    <td>{index + 1}</td>
                                    <td>{layout.name}</td>
                                    <td>{layout.typeDescription}</td>
                                    <td>{layout.directionDescription}</td>
                                    <td>
                                        <button className="info-btn" onClick={() => handleInfoClick(layout)}>
                                            <i className="ri-information-line"></i>
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* 상세정보 모달 */}
            {showDetailModal && currentLayout && (
                <div className="modal-overlay active" id="layout-detail-modal">
                    <div className="modal-container">
                        <div className="modal-header">
                            <h3 className="modal-title">레이아웃 상세정보</h3>
                            <button className="modal-close" onClick={() => setShowDetailModal(false)}>&times;</button>
                        </div>
                        <div className="modal-body">
                            <div className="detail-item-container">
                                <span className="detail-label">레이아웃명</span>
                                <span className="detail-content">{currentLayout.name}</span>
                            </div>
                            <div className="detail-item-container">
                                <span className="detail-label">타입</span>
                                <span className="detail-content">{currentLayout.typeDescription}</span>
                            </div>
                            <div className="detail-item-container">
                                <span className="detail-label">방향</span>
                                <span className="detail-content">{currentLayout.directionDescription}</span>
                            </div>
                            <div className="detail-item-container">
                                <div className="detail-row">
                                    <div className="detail-column">
                                        <span className="detail-label">테마별 기본 이미지</span>
                                        <div className="modal-data-image">
                                            {currentLayout.customDefaultImageResourceId ? (
                                                <img
                                                    src={`${VITE_ADMIN_BACKEND_API_URL}/resource/view/${currentLayout.customDefaultImageResourceId}`}
                                                    alt="custom_default"
                                                />
                                            ) : (
                                                <img
                                                    src="images/layoutData/default_layout.png"
                                                    alt="default_image"
                                                />
                                            )}
                                        </div>
                                    </div>
                                    <div className="detail-column">
                                        <span className="detail-label">테마별 호버 이미지</span>
                                        <div className="modal-data-image">
                                            {currentLayout.customHoverImageResourceId ? (
                                                <img
                                                    src={`${VITE_ADMIN_BACKEND_API_URL}/resource/view/${currentLayout.customHoverImageResourceId}`}
                                                    alt="custom_hover"
                                                />
                                            ) : (
                                                <img
                                                    src="images/layoutData/default_layout_hover.png"
                                                    alt="default_hover"
                                                />
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button 
                                className="modal-btn modal-btn-primary" 
                                onClick={() => setShowDetailModal(false)}
                            >
                                확인
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PresetLayoutSetting;
